import { useState, useEffect, type ReactElement } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import ForestAesthetics from './forest/ForestAesthetics';
import OceanAesthetics from './ocean/OceanAesthetics';
import CyberpunkAesthetics from './cyberpunk/CyberpunkAesthetics';
import VaporwaveAesthetics from './vaporwave/VaporwaveAesthetics';
import SynthwaveAesthetics from './synthwave/SynthwaveAesthetics';
import SunsetAesthetics from './sunset/SunsetAesthetics';
import ArcticAesthetics from './arctic/ArcticAesthetics';
import SakuraAesthetics from './sakura/SakuraAesthetics';

type Theme = 'light' | 'dark' | 'cyberpunk' | 'forest' | 'ocean' | 'monochrome' | string;

const THEME_COMPONENTS: Record<string, () => ReactElement> = {
  cyberpunk: () => <CyberpunkAesthetics />,
  forest: () => <ForestAesthetics />,
  ocean: () => <OceanAesthetics />,
  vaporwave: () => <VaporwaveAesthetics />,
  synthwave: () => <SynthwaveAesthetics />,
  sunset: () => <SunsetAesthetics />,
  arctic: () => <ArcticAesthetics />,
  sakura: () => <SakuraAesthetics />,
};

/**
 * Observes the data-theme attribute on the <html> element and returns the current theme.
 * @returns {Theme} The current theme name.
 */
const useCurrentTheme = (): Theme => {
  const [theme, setTheme] = useState<Theme>('');

  useEffect(() => {
    const root = document.documentElement;

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-theme'
        ) {
          const newTheme = root.getAttribute('data-theme') || '';
          console.log('[ThemeAestheticsController] Theme changed to:', newTheme);
          setTheme(newTheme);
        }
      }
    });

    // Set initial theme
    const initialTheme = root.getAttribute('data-theme') || '';
    console.log('[ThemeAestheticsController] Initial theme:', initialTheme);
    setTheme(initialTheme);

    observer.observe(root, { attributes: true });

    return () => observer.disconnect();
  }, []);

  return theme;
};

/**
 * A controller component that renders the appropriate aesthetic visuals
 * based on the current `data-theme` attribute on the <html> tag.
 *
 * Automatically disables animations on mobile devices for better performance.
 */
const ThemeAestheticsController = () => {
  const currentTheme = useCurrentTheme();
  const isMobile = useIsMobile();

  if (isMobile) {
    console.log('[ThemeAestheticsController] Mobile detected - animations disabled');
    return null;
  }

  console.log('[ThemeAestheticsController] Rendering aesthetics for theme:', currentTheme);
  const themeFactory = THEME_COMPONENTS[currentTheme];
  const aesthetics = themeFactory ? themeFactory() : null;
  console.log('[ThemeAestheticsController] Aesthetics component:', aesthetics ? 'Present' : 'None');

  // Render with CSS fade animation (not Framer Motion to prevent conflicts with Radix UI)
  return aesthetics ? (
    <div
      className="aesthetics-fade-in fixed inset-0 pointer-events-none"
    >
      {aesthetics}
    </div>
  ) : null;
};

export default ThemeAestheticsController;
